import { DUCK_BUFFER_MS, type Lane } from "../constants";

export type MoveIntent = "left" | "right" | "jump" | "duck" | "pause" | "confirm" | null;

export interface BufferedJump {
  pressedAt: number;
}

export class InputController {
  private left = false;
  private right = false;
  private up = false;
  private down = false;
  private escape = false;
  private confirm = false;

  private prevLeft = false;
  private prevRight = false;
  private prevUp = false;
  private prevDown = false;
  private prevEscape = false;
  private prevConfirm = false;

  private jumpBufferUntil = -1;
  private duckBufferUntil = -1;
  private queuedLane: -1 | 0 | 1 = 0;
  private readonly bufferMs: number;
  private readonly duckBufferMs: number;

  constructor(bufferMs: number, duckBufferMs = DUCK_BUFFER_MS) {
    this.bufferMs = bufferMs;
    this.duckBufferMs = duckBufferMs;
  }

  setHeld(keys: {
    left: boolean;
    right: boolean;
    up: boolean;
    down: boolean;
    escape: boolean;
    confirm: boolean;
  }): void {
    this.left = keys.left;
    this.right = keys.right;
    this.up = keys.up;
    this.down = keys.down;
    this.escape = keys.escape;
    this.confirm = keys.confirm;
  }

  pulseJump(elapsedMs: number): void {
    this.jumpBufferUntil = elapsedMs + this.bufferMs;
  }

  pulseDuck(elapsedMs: number): void {
    this.duckBufferUntil = elapsedMs + this.duckBufferMs;
  }

  pulseLane(delta: -1 | 1): void {
    this.queuedLane = delta;
  }

  extendBuffersUntil(untilMs: number): void {
    if (this.jumpBufferUntil >= 0) {
      this.jumpBufferUntil = Math.max(this.jumpBufferUntil, untilMs);
    }
    if (this.duckBufferUntil >= 0) {
      this.duckBufferUntil = Math.max(this.duckBufferUntil, untilMs);
    }
  }

  poll(elapsedMs: number): {
    laneDelta: -1 | 0 | 1;
    jump: boolean;
    duck: boolean;
    pause: boolean;
    confirm: boolean;
  } {
    const leftPressed = this.left && !this.prevLeft;
    const rightPressed = this.right && !this.prevRight;
    const upPressed = this.up && !this.prevUp;
    const downPressed = this.down && !this.prevDown;
    const pause = this.escape && !this.prevEscape;
    const confirm = this.confirm && !this.prevConfirm;

    if (upPressed) {
      this.jumpBufferUntil = elapsedMs + this.bufferMs;
    }
    if (downPressed) {
      this.duckBufferUntil = elapsedMs + this.duckBufferMs;
    }

    const jump = this.jumpBufferUntil >= elapsedMs;
    const duck = this.duckBufferUntil >= elapsedMs;

    this.prevLeft = this.left;
    this.prevRight = this.right;
    this.prevUp = this.up;
    this.prevDown = this.down;
    this.prevEscape = this.escape;
    this.prevConfirm = this.confirm;

    let laneDelta: -1 | 0 | 1 = 0;
    if (this.queuedLane !== 0) {
      laneDelta = this.queuedLane;
      this.queuedLane = 0;
    } else if (leftPressed && !rightPressed) {
      laneDelta = -1;
    } else if (rightPressed && !leftPressed) {
      laneDelta = 1;
    }

    return {
      laneDelta,
      jump,
      duck,
      pause,
      confirm,
    };
  }

  consumeJump(): void {
    this.jumpBufferUntil = -1;
  }

  consumeDuck(): void {
    this.duckBufferUntil = -1;
  }

  clampLane(lane: Lane, delta: -1 | 0 | 1): Lane {
    const next = lane + delta;
    if (next < -1) {
      return -1;
    }
    if (next > 1) {
      return 1;
    }
    return next as Lane;
  }
}
